/**
 * Code Examples Step Component
 *
 * Step 4: Show code examples in multiple languages.
 *
 * @module components/onboarding/CodeExamplesStep
 */

import { useState } from 'react';
import { Button } from '@/components/ui';
import { Copy, Check, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_URL } from '@/lib/config';

// =============================================================================
// Types
// =============================================================================

export interface CodeExamplesStepProps {
  /** The API key to include in examples */
  apiKey: string;
  /** Callback when user continues */
  onContinue: () => void;
}

type Language = 'curl' | 'javascript' | 'python' | 'go' | 'ruby' | 'php';

// =============================================================================
// Code Templates
// =============================================================================

const getCodeExamples = (apiKey: string): Record<Language, string> => ({
  curl: `curl "${API_URL}/v1/geo/lookup" \\
  -H "X-API-Key: ${apiKey}" \\
  --compressed`,

  javascript: `const response = await fetch('${API_URL}/v1/geo/lookup', {
  headers: { 'X-API-Key': '${apiKey}' }
});
const data = await response.json();
console.log(data);`,

  python: `import requests

response = requests.get(
    '${API_URL}/v1/geo/lookup',
    headers={'X-API-Key': '${apiKey}'}
)
data = response.json()
print(data)`,

  go: `package main

import (
    "fmt"
    "net/http"
    "io"
)

func main() {
    req, _ := http.NewRequest("GET", "${API_URL}/v1/geo/lookup", nil)
    req.Header.Set("X-API-Key", "${apiKey}")

    resp, _ := http.DefaultClient.Do(req)
    defer resp.Body.Close()

    body, _ := io.ReadAll(resp.Body)
    fmt.Println(string(body))
}`,

  ruby: `require 'net/http'
require 'json'

uri = URI('${API_URL}/v1/geo/lookup')
req = Net::HTTP::Get.new(uri)
req['X-API-Key'] = '${apiKey}'

res = Net::HTTP.start(uri.hostname, uri.port, use_ssl: true) { |http| http.request(req) }
data = JSON.parse(res.body)
puts data`,

  php: `<?php
$ch = curl_init('${API_URL}/v1/geo/lookup');
curl_setopt($ch, CURLOPT_HTTPHEADER, ['X-API-Key: ${apiKey}']);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);

$data = json_decode($response, true);
print_r($data);`,
});

const LANGUAGE_LABELS: Record<Language, string> = {
  curl: 'cURL',
  javascript: 'JavaScript',
  python: 'Python',
  go: 'Go',
  ruby: 'Ruby',
  php: 'PHP',
};

const LANGUAGES: Language[] = [
  'curl',
  'javascript',
  'python',
  'go',
  'ruby',
  'php',
];

// =============================================================================
// Component
// =============================================================================

export function CodeExamplesStep({ apiKey, onContinue }: CodeExamplesStepProps) {
  const [activeLanguage, setActiveLanguage] = useState<Language>('curl');
  const [copied, setCopied] = useState(false);

  const codeExamples = getCodeExamples(apiKey);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeExamples[activeLanguage]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          🚀 Start building
        </h2>
        <p className="text-gray-500 mt-2">
          Copy a code example to integrate the Geolocation API into your project, or click Continue to wrap up onboarding and take the next steps.
        </p>
      </div>

      {/* Language tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 overflow-x-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang}
              onClick={() => setActiveLanguage(lang)}
              className={cn(
                'py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors',
                activeLanguage === lang
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {LANGUAGE_LABELS[lang]}
            </button>
          ))}
        </nav>
      </div>

      {/* Code block */}
      <div className="relative">
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 z-10 flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-success-400" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              Copy
            </>
          )}
        </button>
        <pre className="p-4 bg-gray-900 rounded-lg overflow-x-auto text-sm">
          <code className="text-green-400 whitespace-pre">
            {codeExamples[activeLanguage]}
          </code>
        </pre>
      </div>

      <Button
        onClick={onContinue}
        rightIcon={<ArrowRight className="h-4 w-4" />}
      >
        Continue
      </Button>
    </div>
  );
}
